import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, TextField, Chip, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, TablePagination,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { logSourcesAPI } from '../services/api';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const ACTION = { added: 'success', imported: 'default', changed: 'info', enabled: 'success', disabled: 'warning', removed: 'warning' };

function SourceDialog({ source, meta, customerId, onClose, onSaved }) {
  const [customer, setCustomer] = useState(source?.customer_id || customerId || '');
  const [form, setForm] = useState({
    environment: source?.environment || 'Development', log_type: source?.log_type || 'User', server_name: source?.server_name || '',
    local_path: source?.local_path || '', app_name: source?.app_name || '', enabled: source ? source.enabled : true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const res = source ? await logSourcesAPI.update(source.id, form) : await logSourcesAPI.add(customer, form);
      onSaved(source ? 'Log source updated' : 'Log source added', res.data?.published, res.data?.tags);
    } catch (err) { setError(errText(err, 'Could not save the log source')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{source ? 'Edit log source' : 'Add log source'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField select label="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)} disabled={!!source} slotProps={NATIVE} required>
            <option value="" />
            {meta.customers.map((c) => <option key={c.id} value={c.id}>{c.cust_code} - {c.cust_name}</option>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth label="Environment" value={form.environment} onChange={set('environment')} slotProps={NATIVE}>
              {meta.environments.map((e) => <option key={e} value={e}>{e}</option>)}
            </TextField>
            <TextField select fullWidth label="Log type" value={form.log_type} onChange={set('log_type')} slotProps={NATIVE}>
              {meta.log_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </TextField>
          </Box>
          <TextField label="Server name" required value={form.server_name} onChange={set('server_name')} helperText="The server that holds the folder, e.g. EAU-DEV-APP01" />
          <TextField label="Folder on the server" required value={form.local_path} onChange={set('local_path')}
            helperText="As that server sees it, e.g. E:\STARLIMS\STARLIMS11.EA.Dev\Log\Users" />
          <TextField label="Application folder (reference)" value={form.app_name} onChange={set('app_name')} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || (!source && !customer)}>{busy ? 'Saving...' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveDialog({ source, onClose, onRemoved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const remove = async () => {
    setBusy(true);
    try { const res = await logSourcesAPI.remove(source.id); onRemoved(res.data?.published, res.data?.tags); } catch (err) { setError(errText(err, 'Could not remove the log source')); setBusy(false); }
  };
  return (
    <Dialog open onClose={() => !busy && onClose()}>
      <DialogTitle>Remove log source?</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography>{source.cust_code} {source.environment} {source.log_type}: {source.server_name} {source.local_path} will be removed from the list. It stays in the history, and logs already in S3 are kept.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="error" variant="contained" onClick={remove} disabled={busy}>Remove</Button>
      </DialogActions>
    </Dialog>
  );
}

function HistoryDialog({ source, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { logSourcesAPI.history(source ? { source_id: source.id } : {}).then((r) => setRows(r.data)).catch(() => setRows([])); }, [source]);
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{source ? `History - ${source.server_name} ${source.local_path}` : 'History - all log sources'}</DialogTitle>
      <DialogContent>
        {rows === null ? <CircularProgress size={24} /> : (
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell>When</TableCell><TableCell>Action</TableCell><TableCell>Customer</TableCell><TableCell>Source</TableCell><TableCell>Detail</TableCell><TableCell>By</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{fmtTime(h.changed_at)}</TableCell>
                    <TableCell><Chip size="small" label={h.action} color={ACTION[h.action] || 'default'} /></TableCell>
                    <TableCell>{h.cust_code}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{h.environment} / {h.log_type} / {h.server_name}<Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>{h.local_path}</Typography></TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{h.detail || '-'}</TableCell>
                    <TableCell>{h.changed_by}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No history.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function LogSources() {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState(null);
  const [customerId, setCustomerId] = useState('');
  const [environment, setEnvironment] = useState('');
  const [logType, setLogType] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [dialog, setDialog] = useState(null);       // { type: 'add' | 'edit' | 'remove' | 'history', source }
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadMeta = useCallback(() => logSourcesAPI.meta().then((r) => setMeta(r.data)).catch((err) => setError(errText(err, 'Could not load log sources'))), []);
  const load = useCallback(async () => {
    try {
      const res = await logSourcesAPI.list({ customer_id: customerId || undefined, environment: environment || undefined, log_type: logType || undefined, q: search.trim() || undefined });
      setRows(res.data); setPage(0);
    } catch (err) { setError(errText(err, 'Could not load log sources')); setRows([]); }
  }, [customerId, environment, logType, search]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const NOT_PUBLISHED = 'Saved, but the servers could not be told yet. Use "Publish to servers" to try again.';
  const saved = (message, published, tags) => {
    setDialog(null); load(); loadMeta();
    const names = (...states) => Object.entries(tags || {}).filter(([, v]) => states.includes(v)).map(([k]) => k).join(', ');
    const missing = names('not_found'), failed = names('error'), on = names('tagged'), off = names('untagged');
    const problems = [published === false && NOT_PUBLISHED,
      missing && `${missing} was not found in AWS, so it will not sync until it is tagged LogSync=true.`,
      failed && `${failed} could not be tagged for syncing. Tag it LogSync=true by hand.`].filter(Boolean);
    if (problems.length) { setSuccess(null); setError(problems.join(' ')); return; }
    setError(null);
    setSuccess([message, on && `${on} is now set to sync.`, off && `${off} no longer syncs.`].filter(Boolean).join('. ').replace(/\.\./g, '.'));
  };
  const toggle = async (s) => {
    try {
      const res = await logSourcesAPI.update(s.id, { enabled: !s.enabled });
      saved(s.enabled ? 'Log source switched off' : 'Log source switched on', res.data?.published, res.data?.tags);
    } catch (err) { setError(errText(err, 'Could not change the log source')); }
  };
  const publishAll = async () => {
    setPublishing(true);
    try { const res = await logSourcesAPI.publish(); setError(null); setSuccess(`Published the settings of ${res.data.published} servers.`); }
    catch (err) { setError(errText(err, 'Could not publish to the servers')); }
    setPublishing(false);
  };
  const sm = meta?.summary;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Customer Log Sources</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Where each customer's logs live: the folder on each server, per environment. Enabled sources are copied to S3 every 15 minutes under customer/CODE/logs/environment/server/type/. Changes reach the servers automatically; Publish to servers sends everything again.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {sm && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`${sm.sources} sources`} /><Chip label={`${sm.enabled} enabled`} color="success" variant="outlined" />
          <Chip label={`${sm.customers} customers`} variant="outlined" /><Chip label={`${sm.servers} servers`} variant="outlined" />
        </Box>
      )}
      {meta && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField select size="small" label="Customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 220 }}>
            <option value="">All customers</option>
            {meta.customers.map((c) => <option key={c.id} value={c.id}>{c.cust_code} - {c.cust_name}</option>)}
          </TextField>
          <TextField select size="small" label="Environment" value={environment} onChange={(e) => setEnvironment(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 150 }}>
            <option value="">All</option>{meta.environments.map((e) => <option key={e} value={e}>{e}</option>)}
          </TextField>
          <TextField select size="small" label="Log type" value={logType} onChange={(e) => setLogType(e.target.value)} slotProps={NATIVE} sx={{ minWidth: 130 }}>
            <option value="">All</option>{meta.log_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </TextField>
          <TextField size="small" label="Search server or path" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 180 }} />
          <Button startIcon={<CloudUploadIcon />} onClick={publishAll} disabled={publishing}>{publishing ? 'Publishing...' : 'Publish to servers'}</Button>
          <Button startIcon={<HistoryIcon />} onClick={() => setDialog({ type: 'history', source: null })}>History</Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialog({ type: 'add' })}>Add log source</Button>
        </Box>
      )}
      {rows === null ? <CircularProgress /> : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow><TableCell>Customer</TableCell><TableCell>Environment</TableCell><TableCell>Type</TableCell><TableCell>Server</TableCell><TableCell>Folder</TableCell><TableCell>Enabled</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {rows.slice(page * 50, page * 50 + 50).map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>{s.cust_code}</TableCell><TableCell>{s.environment}</TableCell><TableCell>{s.log_type}</TableCell><TableCell>{s.server_name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{s.local_path}</TableCell>
                    <TableCell><Switch size="small" checked={s.enabled} onChange={() => toggle(s)} slotProps={{ input: { 'aria-label': `Enabled ${s.server_name} ${s.local_path}` } }} /></TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="History"><IconButton size="small" aria-label={`History ${s.id}`} onClick={() => setDialog({ type: 'history', source: s })}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" aria-label={`Edit ${s.id}`} onClick={() => setDialog({ type: 'edit', source: s })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Remove"><IconButton size="small" aria-label={`Remove ${s.id}`} onClick={() => setDialog({ type: 'remove', source: s })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={7}><Typography color="text.secondary">No log sources match.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={rows.length} page={page} rowsPerPage={50} rowsPerPageOptions={[50]} onPageChange={(_, p) => setPage(p)} />
        </Paper>
      )}
      {dialog?.type === 'add' && <SourceDialog meta={meta} customerId={customerId} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.type === 'edit' && <SourceDialog meta={meta} source={dialog.source} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.type === 'remove' && <RemoveDialog source={dialog.source} onClose={() => setDialog(null)} onRemoved={(published, tags) => saved('Log source removed', published, tags)} />}
      {dialog?.type === 'history' && <HistoryDialog source={dialog.source} onClose={() => setDialog(null)} />}
    </Box>
  );
}

export default LogSources;
